"use client";

import { FormEvent, useEffect, useState } from "react";

type FormState = {
  lang: "de" | "en";
  title: string;
  problem: string;
  targetGroup: string;
  uniqueValue: string;
  periodMonths: number;
  revenueEstimate: number;
  fixedCosts: number;
  variableCosts: number;
  initialInvestment: number;
  notes: string;
};

const STORAGE_KEY = "vtob_idea_draft";
const LANG_KEY = "vtob_lang";

type PlanView = {
  executiveSummary?: string;
  valueProposition?: string;
  marketAndTargetGroup?: string;
  businessModel?: {
    horizonMonths?: number;
    revenueEstimatePerMonth?: number;
    fixedCostsPerMonth?: number;
    variableCostsPerMonth?: number;
    monthlyGrossMargin?: number;
    initialInvestment?: number;
    runwayMonths?: number;
  };
  goToMarket?: string[];
  risks?: string[];
  risksDetailed?: string[];
  costModel?: {
    initialInvestmentRange?: string;
    fixedCostRangeMonthly?: string;
    variableCostRangeMonthly?: string;
    scenarios?: Array<{
      name?: string;
      revenueEstimatePerMonth?: number;
      fixedCostsPerMonth?: number;
      variableCostsPerMonth?: number;
      monthlyMargin?: number;
      runwayMonths?: number;
    }>;
    assumptions?: string[];
  };
  comparableExamples?: Array<{
    name?: string;
    mvpTimelineWeeks?: string;
    initialInvestmentRange?: string;
    monthlyCostRange?: string;
    description?: string;
  }>;
  next90Days?: string[];
};

const copy = {
  de: {
    title: "Plan generieren",
    desc: "Dieser MVP-Schritt erzeugt aus Idee und Finanzannahmen einen ersten, strukturierten Businessplan-Entwurf.",
    base: "Basis-Einstellungen",
    lang: "Sprache",
    ideaTitle: "Titel",
    problem: "Problem",
    target: "Zielgruppe",
    unique: "Unique Value (optional)",
    horizon: "Planhorizont (Monate)",
    revenue: "Einnahmen pro Monat (Schaetzung)",
    fixed: "Fixkosten pro Monat",
    variable: "Variable Kosten pro Monat",
    invest: "Initiale Investition",
    notes: "Notizen (optional)",
    generating: "Generiere...",
    generate: "Entwurf generieren",
    back: "Zurueck zu Idee erfassen",
    result: "Ergebnis",
    source: "Quelle",
    project: "Projekt-ID",
    version: "Version",
    exec: "Executive Summary",
    value: "Value Proposition",
    market: "Markt & Zielgruppe",
    model: "Business Model",
    go: "Go-to-Market",
    risks: "Risiken",
    next90: "Naechste 90 Tage",
    openProject: "Projektdetail oeffnen",
    horizonLbl: "Horizont",
    revenueLbl: "Einnahmen",
    fixedLbl: "Fixkosten",
    variableLbl: "Variable Kosten",
    marginLbl: "Bruttomarge",
    investmentLbl: "Initiale Investition",
    runwayLbl: "Runway",
    costModel: "Kostenmodell",
    costInitRange: "Initiale Investition (Bandbreite)",
    costFixedRange: "Fixkosten pro Monat (Bandbreite)",
    costVarRange: "Variable Kosten pro Monat (Bandbreite)",
    scenarios: "Szenarien",
    assumptions: "Annahmen",
    risksDetailed: "Risikoregister",
    comparable: "Vergleichbare Beispiele",
    timeline: "MVP-Zeitleiste (Wochen)",
    monthlyCostRange: "Monatliche Kostenbandbreite",
  },
  en: {
    title: "Generate plan",
    desc: "This MVP step creates a first structured business plan draft from your idea and financial assumptions.",
    base: "Core settings",
    lang: "Language",
    ideaTitle: "Title",
    problem: "Problem",
    target: "Target group",
    unique: "Unique value (optional)",
    horizon: "Planning horizon (months)",
    revenue: "Estimated monthly revenue",
    fixed: "Fixed monthly costs",
    variable: "Variable monthly costs",
    invest: "Initial investment",
    notes: "Notes (optional)",
    generating: "Generating...",
    generate: "Generate draft",
    back: "Back to idea capture",
    result: "Result",
    source: "Source",
    project: "Project ID",
    version: "Version",
    exec: "Executive summary",
    value: "Value proposition",
    market: "Market & target group",
    model: "Business model",
    go: "Go-to-market",
    risks: "Risks",
    next90: "Next 90 days",
    openProject: "Open project detail",
    horizonLbl: "Horizon",
    revenueLbl: "Revenue",
    fixedLbl: "Fixed costs",
    variableLbl: "Variable costs",
    marginLbl: "Gross margin",
    investmentLbl: "Initial investment",
    runwayLbl: "Runway",
    costModel: "Cost model",
    costInitRange: "Initial investment (range)",
    costFixedRange: "Fixed monthly costs (range)",
    costVarRange: "Variable monthly costs (range)",
    scenarios: "Scenarios",
    assumptions: "Assumptions",
    risksDetailed: "Risk register",
    comparable: "Comparable examples",
    timeline: "MVP timeline (weeks)",
    monthlyCostRange: "Monthly cost range",
  },
} as const;

export default function PlanGenerierenPage() {
  const [lang, setLang] = useState<"de" | "en">("de");
  const [form, setForm] = useState<FormState>({
    lang: "de",
    title: "",
    problem: "",
    targetGroup: "",
    uniqueValue: "",
    periodMonths: 12,
    revenueEstimate: 0,
    fixedCosts: 0,
    variableCosts: 0,
    initialInvestment: 1,
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{
    provider: string;
    projectId: number;
    versionNo: number;
    plan: unknown;
  } | null>(null);

  useEffect(() => {
    const current = window.localStorage.getItem(LANG_KEY) === "en" ? "en" : "de";
    setLang(current);
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<FormState>;
      setForm((prev) => ({
        ...prev,
        lang: parsed.lang === "en" || current === "en" ? "en" : "de",
        title: parsed.title || prev.title,
        problem: parsed.problem || prev.problem,
        targetGroup: parsed.targetGroup || prev.targetGroup,
        uniqueValue: parsed.uniqueValue || prev.uniqueValue,
      }));
    } catch {
      // ignore malformed local data
    }
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      const nextLang = detail === "en" ? "en" : "de";
      setLang(nextLang);
      setForm((prev) => ({ ...prev, lang: nextLang }));
    };
    window.addEventListener("vtob-lang-change", handler);
    return () => window.removeEventListener("vtob-lang-change", handler);
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const response = await fetch("/api/plan/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Plan konnte nicht generiert werden.");
      }
      setResult({
        provider: String(data.provider),
        projectId: Number(data.projectId),
        versionNo: Number(data.versionNo),
        plan: data.plan,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
    } finally {
      setLoading(false);
    }
  }

  const t = copy[lang];
  const plan = (result?.plan || {}) as PlanView;

  return (
    <main className="vtob-container">
      <h1 style={{ marginBottom: 8 }}>{t.title}</h1>
      <p style={{ marginBottom: 24, color: "#555" }}>{t.desc}</p>

      <section className="vtob-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>{t.base}</h2>
        <form className="vtob-grid" onSubmit={onSubmit}>
          <label>
            {t.lang}
            <select
              value={form.lang}
              onChange={(e) => {
                const nextLang = e.target.value as "de" | "en";
                window.localStorage.setItem(LANG_KEY, nextLang);
                window.dispatchEvent(new CustomEvent("vtob-lang-change", { detail: nextLang }));
                setForm((prev) => ({ ...prev, lang: nextLang }));
                setLang(nextLang);
              }}
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </label>
          <label>
            {t.ideaTitle}
            <input
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder={t.ideaTitle}
              required
            />
          </label>
          <label>
            {t.problem}
            <textarea
              value={form.problem}
              onChange={(e) => setForm((prev) => ({ ...prev, problem: e.target.value }))}
              rows={3}
              required
            />
          </label>
          <label>
            {t.target}
            <input
              value={form.targetGroup}
              onChange={(e) => setForm((prev) => ({ ...prev, targetGroup: e.target.value }))}
              required
            />
          </label>
          <label>
            {t.unique}
            <textarea
              value={form.uniqueValue}
              onChange={(e) => setForm((prev) => ({ ...prev, uniqueValue: e.target.value }))}
              rows={2}
            />
          </label>
          <label>
            {t.horizon}
            <input
              type="number"
              min={3}
              max={60}
              value={form.periodMonths}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, periodMonths: Number(e.target.value || 12) }))
              }
            />
          </label>
          <label>
            {t.revenue}
            <input
              type="number"
              min={0}
              step="any"
              value={form.revenueEstimate}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, revenueEstimate: Number(e.target.value || 0) }))
              }
            />
          </label>
          <label>
            {t.fixed}
            <input
              type="number"
              min={0}
              step="any"
              value={form.fixedCosts}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, fixedCosts: Number(e.target.value || 0) }))
              }
            />
          </label>
          <label>
            {t.variable}
            <input
              type="number"
              min={0}
              step="any"
              value={form.variableCosts}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, variableCosts: Number(e.target.value || 0) }))
              }
            />
          </label>
          <label>
            {t.invest}
            <input
              type="number"
              min={1}
              step="any"
              value={form.initialInvestment}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  initialInvestment: Math.max(1, Number(e.target.value || 1)),
                }))
              }
            />
          </label>
          <label>
            {t.notes}
            <textarea
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </label>
          <div className="vtob-actions">
            <button className="vtob-btn" type="submit" disabled={loading}>
              {loading ? t.generating : t.generate}
            </button>
            <a className="vtob-link-btn" href="/idee-erfassen">
              {t.back}
            </a>
          </div>
        </form>
      </section>

      {error ? <p style={{ color: "#b00020" }}>{error}</p> : null}

      {result ? (
        <section className="vtob-card">
          <h2 style={{ marginTop: 0 }}>{t.result}</h2>
          <p style={{ margin: "6px 0" }}>
            {t.source}: <strong>{result.provider}</strong> | {t.project}:{" "}
            <strong>{result.projectId}</strong> | Version:{" "}
            <strong>{result.versionNo}</strong>
          </p>
          <p style={{ marginTop: 12 }}>
            <a className="vtob-link-btn" href={`/projekte/${result.projectId}`}>
              {t.openProject}
            </a>
          </p>
          {plan.executiveSummary ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.exec}</h3>
              <p>{plan.executiveSummary}</p>
            </div>
          ) : null}
          {plan.valueProposition ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.value}</h3>
              <p>{plan.valueProposition}</p>
            </div>
          ) : null}
          {plan.marketAndTargetGroup ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.market}</h3>
              <p>{plan.marketAndTargetGroup}</p>
            </div>
          ) : null}
          {plan.businessModel ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.model}</h3>
              <ul style={{ marginLeft: 18 }}>
                <li>{t.horizonLbl}: {plan.businessModel.horizonMonths ?? "-"}</li>
                <li>{t.revenueLbl}: {plan.businessModel.revenueEstimatePerMonth ?? "-"}</li>
                <li>{t.fixedLbl}: {plan.businessModel.fixedCostsPerMonth ?? "-"}</li>
                <li>{t.variableLbl}: {plan.businessModel.variableCostsPerMonth ?? "-"}</li>
                <li>{t.marginLbl}: {plan.businessModel.monthlyGrossMargin ?? "-"}</li>
                <li>{t.investmentLbl}: {plan.businessModel.initialInvestment ?? "-"}</li>
                <li>{t.runwayLbl}: {plan.businessModel.runwayMonths ?? "-"}</li>
              </ul>
            </div>
          ) : null}
          {Array.isArray(plan.goToMarket) && plan.goToMarket.length ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.go}</h3>
              <ul style={{ marginLeft: 18 }}>
                {plan.goToMarket.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {Array.isArray(plan.risks) && plan.risks.length ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.risks}</h3>
              <ul style={{ marginLeft: 18 }}>
                {plan.risks.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {Array.isArray(plan.risksDetailed) && plan.risksDetailed.length ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.risksDetailed}</h3>
              <ul style={{ marginLeft: 18 }}>
                {plan.risksDetailed.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {plan.costModel ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.costModel}</h3>
              <ul style={{ marginLeft: 18 }}>
                <li>{t.costInitRange}: {plan.costModel.initialInvestmentRange ?? "-"}</li>
                <li>{t.costFixedRange}: {plan.costModel.fixedCostRangeMonthly ?? "-"}</li>
                <li>{t.costVarRange}: {plan.costModel.variableCostRangeMonthly ?? "-"}</li>
              </ul>
              {Array.isArray(plan.costModel.scenarios) && plan.costModel.scenarios.length ? (
                <div style={{ marginTop: 10 }}>
                  <h4>{t.scenarios}</h4>
                  <ul style={{ marginLeft: 18 }}>
                    {plan.costModel.scenarios.map((s, idx) => (
                      <li key={`${s.name || "scenario"}-${idx}`}>
                        <strong>{s.name || "-"}</strong>: {t.revenueLbl} {s.revenueEstimatePerMonth ?? "-"} | {t.fixedLbl}{" "}
                        {s.fixedCostsPerMonth ?? "-"} | {t.variableLbl} {s.variableCostsPerMonth ?? "-"} |{" "}
                        {t.marginLbl} {s.monthlyMargin ?? "-"} | {t.runwayLbl} {s.runwayMonths ?? "-"}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {Array.isArray(plan.costModel.assumptions) && plan.costModel.assumptions.length ? (
                <div style={{ marginTop: 10 }}>
                  <h4>{t.assumptions}</h4>
                  <ul style={{ marginLeft: 18 }}>
                    {plan.costModel.assumptions.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
          {Array.isArray(plan.comparableExamples) && plan.comparableExamples.length ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.comparable}</h3>
              <ul style={{ marginLeft: 18 }}>
                {plan.comparableExamples.map((item, idx) => (
                  <li key={`${item.name || "cmp"}-${idx}`}>
                    <strong>{item.name || "-"}</strong>: {t.timeline} {item.mvpTimelineWeeks || "-"} |{" "}
                    {t.investmentLbl} {item.initialInvestmentRange || "-"} | {t.monthlyCostRange}{" "}
                    {item.monthlyCostRange || "-"}{item.description ? ` - ${item.description}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {Array.isArray(plan.next90Days) && plan.next90Days.length ? (
            <div style={{ marginTop: 16 }}>
              <h3>{t.next90}</h3>
              <ul style={{ marginLeft: 18 }}>
                {plan.next90Days.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
